import { formatSessionClockParts, type SessionClockParts } from '@/lib/sessionTimeRange'

function isArabicLocale(locale: string) {
  return locale.startsWith('ar')
}

function clockGridStyle(locale: string) {
  return {
    display: 'inline-grid',
    gridTemplateColumns: 'auto auto',
    direction: isArabicLocale(locale) ? 'rtl' : 'ltr',
    columnGap: '0.35em',
    alignItems: 'baseline',
    whiteSpace: 'nowrap',
  } as const
}

interface SessionClockTimeProps {
  value?: string | null
  locale: string
  className?: string
  fallback?: string
}

export function SessionClockTime({
  value,
  locale,
  className = '',
  fallback = '—',
}: SessionClockTimeProps) {
  if (!value?.trim()) return <span className={className}>{fallback}</span>

  const parts = formatSessionClockParts(value, locale)
  return <SessionClockPartsDisplay parts={parts} locale={locale} className={className} />
}

export function SessionClockPartsDisplay({
  parts,
  locale,
  className = '',
}: {
  parts: SessionClockParts
  locale: string
  className?: string
}) {
  if (!parts.period) {
    return (
      <span className={`ev-reg-session-clock tabular-nums ${className}`.trim()} dir="ltr">
        {parts.clock}
      </span>
    )
  }

  return (
    <span
      className={`ev-reg-session-clock tabular-nums ${className}`.trim()}
      style={clockGridStyle(locale)}
    >
      <span className="ev-reg-session-clock-value" dir="ltr">
        {parts.clock}
      </span>
      <span className="ev-reg-session-clock-period">{parts.period}</span>
    </span>
  )
}

interface SessionTimeRangeProps {
  start?: string | null
  end?: string | null
  locale: string
  className?: string
}

export function SessionTimeRange({ start, end, locale, className = '' }: SessionTimeRangeProps) {
  const from = start?.trim() ? formatSessionClockParts(start, locale) : null
  const to = end?.trim() ? formatSessionClockParts(end, locale) : null
  const rtl = isArabicLocale(locale)

  if (!from && !to) return null
  if (from && !to) return <SessionClockPartsDisplay parts={from} locale={locale} className={className} />
  if (!from && to) return <SessionClockPartsDisplay parts={to} locale={locale} className={className} />

  return (
    <span
      className={`ev-reg-session-clock tabular-nums ${className}`.trim()}
      style={{
        display: 'inline-flex',
        direction: rtl ? 'rtl' : 'ltr',
        alignItems: 'baseline',
        gap: '0.5em',
        whiteSpace: 'nowrap',
      }}
    >
      <SessionClockPartsDisplay parts={from!} locale={locale} />
      <span>–</span>
      <SessionClockPartsDisplay parts={to!} locale={locale} />
    </span>
  )
}
